/**
 * Activity Table
 *
 * Table displaying emission activity records with actions.
 */

import { type ReactNode } from 'react';
import { Table, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap';
import { ArrowUpDown, ArrowUp, ArrowDown, Inbox } from 'lucide-react';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { EmptyStateCard } from './EmptyStateCard';
import {
  ActivityRecord,
  EmissionScope,
  CalculationStatus,
  CalculationNoticeType,
  Scope2Method,
  type CalculationNotice,
} from '../types/emissions';
import { getActivitySubtype, getActivityUnitLabel } from '../utils/activityUtils';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { SCOPE_TAILWIND } from '../config/scopeBranding';
import { ACTIVITY_TYPE_COLORS, ACTIVITY_TYPE_FA_ICONS, ACTIVITY_TYPE_LABELS } from '../config/activityTypeBranding';
import { DATA_QUALITY_COLORS, DATA_QUALITY_ICONS } from '../config/dataQualityBranding';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';


interface QueryPeriod {
  start: string;
  end: string;
}

interface ActivityTableProps {
  activities: ActivityRecord[];
  isLoading: boolean;
  onEdit?: (activityId: string) => void;
  onDelete?: (activityId: string) => void;
  onRecalculate?: (activityId: string) => void;
  onViewCalculation?: (activity: ActivityRecord) => void;
  onExport?: (activityId: string) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  recalculatingActivityId?: string | null;
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  getReportingLevelLabel?: (initiativeId: string) => React.ReactNode;
  scope2Method?: Scope2Method;
  /** When provided, shows a prorated CO₂e column for activities spanning beyond the query period. */
  queryPeriod?: QueryPeriod;
  /**
   * Per-column visibility overrides. Defaults match the asset-detail page;
   * the multi-add Review step opts out of `added` and opts in to `factor`.
   */
  columns?: {
    asset?: boolean;
    added?: boolean;
    factor?: boolean;
  };
  /**
   * Override for the empty state. Use when filters are active ("no matches") or
   * when scope-specific pages want different copy (e.g. "No Scope 1 emissions yet").
   * When omitted, the default "Add your first activity" CTA is shown.
   */
  emptyState?: ReactNode;
}

/**
 * Compute the fraction of an activity's period that overlaps with the query window.
 * Returns 1 when the activity is fully contained, < 1 when it extends beyond.
 */
const computeProrationRatio = (activityStart: string, activityEnd: string, query: QueryPeriod): number => {
  const aStart = new Date(activityStart).getTime();
  const aEnd = new Date(activityEnd).getTime();
  const qStart = new Date(query.start).getTime();
  const qEnd = new Date(query.end).getTime();

  const totalSpan = aEnd - aStart;
  if (totalSpan <= 0) return 1;

  const overlapStart = Math.max(aStart, qStart);
  const overlapEnd = Math.min(aEnd, qEnd);
  const overlapSpan = Math.max(0, overlapEnd - overlapStart);

  return overlapSpan / totalSpan;
};

const SCOPE_LABELS: Record<EmissionScope, string> = {
  [EmissionScope.Scope1]: 'Scope 1',
  [EmissionScope.Scope2]: 'Scope 2',
  [EmissionScope.Scope3]: 'Scope 3',
};

const STATUS_CONFIG: Record<CalculationStatus, { className: string; icon: string; iconColor: string }> = {
  [CalculationStatus.Calculated]: { className: 'tw:border-emerald-200', icon: 'fa-check-circle', iconColor: 'tw:text-emerald-600' },
  [CalculationStatus.Pending]: { className: 'tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200', icon: 'fa-clock', iconColor: 'tw:text-amber-600' },
  [CalculationStatus.Failed]: { className: 'tw:bg-red-500/10 tw:text-red-700 tw:border-red-200', icon: 'fa-exclamation-circle', iconColor: 'tw:text-red-600' },
  [CalculationStatus.Error]: { className: 'tw:bg-red-500/10 tw:text-red-700 tw:border-red-200', icon: 'fa-exclamation-circle', iconColor: 'tw:text-red-600' },
  [CalculationStatus.RequiresReview]: { className: 'tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200', icon: 'fa-exclamation-triangle', iconColor: 'tw:text-amber-600' },
};

const EMISSION_FACTOR_SOURCE_LABELS: Record<string, string> = {
  defra: 'DEFRA (UK)',
  iea: 'IEA (International)',
  epa: 'EPA (US)',
  climatiq: 'Factors API',
  'climatiq energy api': 'Energy API',
  'climatiq heat api': 'Heat API',
  certificate: 'Certificate',
  direct_contract: 'Direct Contract',
  supplier_specific: 'Supplier Specific',
  residual_mix: 'Residual Mix',
  none: 'Not calculated',
};

const SortIcon = ({ field, sortField, sortDir }: { field: string; sortField?: string; sortDir?: 'asc' | 'desc' }) => {
  if (sortField !== field) return <ArrowUpDown className='d-inline ms-1 opacity-25' style={{ width: 12, height: 12 }} />;
  return sortDir === 'asc'
    ? <ArrowUp className='d-inline ms-1 text-success' style={{ width: 12, height: 12 }} />
    : <ArrowDown className='d-inline ms-1 text-success' style={{ width: 12, height: 12 }} />;
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateShort = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
};

const formatDateRelative = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

const getFailureMessage = (notices?: CalculationNotice[]): string | null => {
  if (!notices || notices.length === 0) {
    return null;
  }
  const errorNotice = notices.find((n) => n.type === CalculationNoticeType.Error);
  return errorNotice?.message ?? null;
};

interface StatusBadgeProps {
  status?: CalculationStatus;
  notices?: CalculationNotice[];
  activityId: string;
}

const StatusBadge = ({ status, notices, activityId }: StatusBadgeProps) => {
  const badgeId = `status-badge-${activityId}`;

  if (!status) {
    return (
      <>
        <i id={badgeId} className='fa fa-question-circle text-ThemeTextLight' style={{ fontSize: '1rem', cursor: 'help' }} />
        <UncontrolledTooltip placement='top' target={badgeId}>Unknown</UncontrolledTooltip>
      </>
    );
  }

  const config = STATUS_CONFIG[status];
  if (!config) {
    return <span>{status}</span>;
  }

  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const failureMessage = status === CalculationStatus.Failed ? getFailureMessage(notices) : null;
  const tooltipText = failureMessage ? `${label}: ${failureMessage}` : label;

  return (
    <>
      <i
        id={badgeId}
        className={cn(`fa ${config.icon}`, config.iconColor)}
        style={{ fontSize: '1rem', cursor: 'help' }}
      />
      <UncontrolledTooltip placement='top' target={badgeId}>
        {tooltipText}
      </UncontrolledTooltip>
    </>
  );
};

export const ActivityTable = ({
  activities,
  isLoading,
  onEdit,
  onDelete,
  onRecalculate,
  onViewCalculation,
  onExport,
  selectedIds,
  onSelectionChange,
  recalculatingActivityId,
  sortField,
  sortDir,
  onSort,
  getReportingLevelLabel,
  scope2Method,
  queryPeriod,
  columns,
  emptyState,
}: ActivityTableProps) => {
  // Per-column visibility — defaults preserve the asset-detail page layout.
  // The multi-add Review step passes `{ added: false, factor: true }`.
  const showAssetCol = columns?.asset ?? true;
  const showAddedCol = columns?.added ?? true;
  const showFactorCol = columns?.factor ?? false;
  const sortableHeader = (label: string, field: string, className?: string) =>
    onSort ? (
      <th className={className} onClick={() => onSort(field)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
        {label}<SortIcon field={field} sortField={sortField} sortDir={sortDir} />
      </th>
    ) : (
      <th className={className}>{label}</th>
    );
  const { formatCompact } = useEmissionsFormatter();
  const showSelection = Boolean(selectedIds && onSelectionChange);

  const handleSelectAll = () => {
    if (!onSelectionChange) {
      return;
    }
    const allCurrentSelected = activities.length > 0 && activities.every((a) => selectedIds?.has(a._id));
    if (allCurrentSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(activities.map((a) => a._id)));
    }
  };

  const handleSelectRow = (activityId: string) => {
    if (!onSelectionChange || !selectedIds) {
      return;
    }
    const newSelected = new Set(selectedIds);
    if (newSelected.has(activityId)) {
      newSelected.delete(activityId);
    } else {
      newSelected.add(activityId);
    }
    onSelectionChange(newSelected);
  };

  if (isLoading) {
    return (
      <div className='text-center py-5'>
        <div className='spinner-border text-success' />
        <p className='text-ThemeTextLight mt-2 mb-0'>Loading activities...</p>
      </div>
    );
  }

  if (activities.length === 0) {
    if (emptyState !== undefined) {
      return <>{emptyState}</>;
    }
    return (
      <EmptyStateCard
        title=''
        message='Add your first activity to start tracking emissions.'
        icon={<Inbox className='tw:size-10' />}
      />
    );
  }

  return (
    <div className='table-responsive'>
      <Table hover className='mb-0 align-middle'>
        <thead className='bg-light'>
          <tr>
            {showSelection && (
              <th style={{ width: 40 }}>
                <input
                  type='checkbox'
                  checked={activities.length > 0 && activities.every((a) => selectedIds?.has(a._id))}
                  onChange={handleSelectAll}
                  aria-label='Select all activities'
                />
              </th>
            )}
            {sortableHeader('Activity', 'activityType')}
            {showAssetCol && <th>Asset</th>}
            {sortableHeader('Period', 'periodStart', 'text-end')}
            {sortableHeader('Quantity', 'quantity', 'text-end')}
            {sortableHeader('CO₂e', 'calculatedCO2e', 'text-end')}
            {showFactorCol && <th>Factor</th>}
            {queryPeriod && <th className='text-end'>Prorated</th>}
            <th className='text-center'>Status</th>
            {showAddedCol && sortableHeader('Added', 'created')}
            <th style={{ width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {activities.map((activity) => {
            const co2eValue = scope2Method === Scope2Method.MarketBased
              ? (activity.calculatedMarketBasedCO2e ?? activity.calculatedCO2e)
              : scope2Method === Scope2Method.LocationBased
                ? (activity.calculatedLocationBasedCO2e ?? activity.calculatedCO2e)
                : activity.calculatedCO2e;
            const co2eFormatted = co2eValue !== undefined && co2eValue !== null
              ? formatCompact(co2eValue)
              : null;

            return (
            <tr
              key={activity._id}
              className={onViewCalculation ? 'tw:cursor-pointer' : undefined}
              onClick={() => onViewCalculation?.(activity)}
            >
              {showSelection && (
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type='checkbox'
                    checked={selectedIds?.has(activity._id) ?? false}
                    onChange={() => handleSelectRow(activity._id)}
                    aria-label={`Select activity ${activity._id}`}
                  />
                </td>
              )}
              <td>
                <div className='d-flex align-items-center gap-2'>
                  <div className='d-flex flex-column align-items-center gap-1 tw:shrink-0'>
                    <div
                      className='tw:rounded tw:flex tw:items-center tw:justify-center'
                      style={{
                        width: 32,
                        height: 32,
                        backgroundColor: `${ACTIVITY_TYPE_COLORS[activity.activityType] ?? ''}15`,
                      }}
                    >
                      <i
                        className={`fa ${ACTIVITY_TYPE_FA_ICONS[activity.activityType] ?? 'fa-leaf'}`}
                        style={{ fontSize: '0.85rem', color: ACTIVITY_TYPE_COLORS[activity.activityType] ?? '#6c757d' }}
                      />
                    </div>
                    <Badge className={cn('tw:text-[10px] tw:px-1 tw:py-0', SCOPE_TAILWIND[activity.scope]?.iconBg, SCOPE_TAILWIND[activity.scope]?.icon)}>
                      {SCOPE_LABELS[activity.scope]}
                    </Badge>
                  </div>
                  <div>
                    {activity.name ? (
                      <>
                        <div className='fw-medium'>
                          {activity.name}
                        </div>
                        <small className='text-ThemeTextLight text-capitalize'>
                          {ACTIVITY_TYPE_LABELS[activity.activityType] ?? activity.activityType}
                        </small>
                      </>
                    ) : getActivitySubtype(activity) ? (
                      <>
                        <div className='fw-medium'>
                          {getActivitySubtype(activity)}
                        </div>
                        <small className='text-ThemeTextLight text-capitalize'>
                          {activity.name ?? ACTIVITY_TYPE_LABELS[activity.activityType] ?? activity.activityType}
                        </small>
                      </>
                    ) : (
                      <div className='fw-medium'>
                        {ACTIVITY_TYPE_LABELS[activity.activityType] ?? activity.activityType}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              {showAssetCol && (
                <td>
                  {activity.assetName ? (
                    <div className='tw:text-xs text-ThemeTextLight'>{activity.assetName}</div>
                  ) : (
                    <span className='tw:text-xs tw:text-muted-foreground'>—</span>
                  )}
                  {getReportingLevelLabel && (
                    <div className='tw:text-xs text-ThemeTextLight'>{getReportingLevelLabel(activity.initiativeId)}</div>
                  )}
                  <div className='tw:text-xs text-ThemeTextLight tw:opacity-60'>{activity.region}</div>
                  {activity.evidenceData && activity.evidenceData.length > 0 ? (
                    <SimpleTooltip
                      text={`${activity.evidenceData.length} evidence file${activity.evidenceData.length === 1 ? '' : 's'} attached`}
                    >
                      <div className='tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:text-ThemeTextMedium tw:mt-1'>
                        <i className='fa fa-paperclip' aria-hidden />
                        {activity.evidenceData.length}
                      </div>
                    </SimpleTooltip>
                  ) : null}
                </td>
              )}
              <td className='text-end'>
                <div className='text-nowrap'>{formatDateShort(activity.periodStart)}</div>
                <div className='text-ThemeTextLight text-nowrap'>to {formatDateShort(activity.periodEnd)}</div>
              </td>
              <td className='text-end text-nowrap'>
                <span className='fw-medium'>{activity.quantity.toLocaleString()}</span>{' '}
                <span className='text-ThemeTextLight'>{getActivityUnitLabel(activity.unit)}</span>
              </td>
              <td className='text-end'>
                {co2eFormatted ? (
                  <Badge variant='secondary' className='tw:text-sm'>
                    {co2eFormatted.formatted} {co2eFormatted.unitCompact}
                  </Badge>
                ) : (
                  <span className='fw-bold'>--</span>
                )}
              </td>
              {showFactorCol && (
                <td>
                  {activity.emissionFactorSource ? (
                    <span className='tw:text-xs tw:text-muted-foreground'>
                      {EMISSION_FACTOR_SOURCE_LABELS[activity.emissionFactorSource.toLowerCase()] ?? activity.emissionFactorSource}
                    </span>
                  ) : (
                    <span className='tw:text-xs tw:text-muted-foreground'>—</span>
                  )}
                </td>
              )}
              {queryPeriod && (() => {
                const ratio = computeProrationRatio(activity.periodStart, activity.periodEnd, queryPeriod);
                const isProrated = ratio < 0.999;
                if (!co2eValue) {
                  return <td className='text-end text-ThemeTextLight tw:text-xs'>—</td>;
                }
                const prorated = formatCompact(co2eValue * ratio);
                if (isProrated) {
                  return (
                    <td className='text-end'>
                      <Badge variant='default' className='tw:text-sm tw:font-semibold'>
                        {prorated.formatted} {prorated.unitCompact}
                      </Badge>
                    </td>
                  );
                }
                return (
                  <td className='text-end'>
                  <Badge variant='default' className='tw:text-sm tw:font-semibold'>
                    {prorated.formatted} {prorated.unitCompact}
                  </Badge>
                  </td>
                );
              })()}
              <td className='text-center'>
                <div className='d-flex align-items-center justify-content-center gap-2'>
                  {(() => {
                    const dq = activity.dataQuality ?? 'estimated';
                    const QualityIcon = DATA_QUALITY_ICONS[dq] ?? DATA_QUALITY_ICONS['estimated'];
                    const qualityColor = DATA_QUALITY_COLORS[dq] ?? DATA_QUALITY_COLORS['estimated'];
                    const qualityLabel = dq.charAt(0).toUpperCase() + dq.slice(1);
                    const sourceLabel = activity.emissionFactorSource
                      ? EMISSION_FACTOR_SOURCE_LABELS[activity.emissionFactorSource.toLowerCase()] || activity.emissionFactorSource
                      : null;
                    const tooltipText = sourceLabel ? `${qualityLabel} · ${sourceLabel}` : qualityLabel;
                    const qualityIconId = `quality-icon-${activity._id}`;
                    return (
                      <>
                        <QualityIcon
                          id={qualityIconId}
                          style={{ width: 16, height: 16, color: qualityColor, cursor: 'help', display: 'inline' }}
                        />
                        <UncontrolledTooltip placement='top' target={qualityIconId}>
                          {tooltipText}
                        </UncontrolledTooltip>
                        {activity.bulkImportId && (() => {
                          const importIconId = `import-icon-${activity._id}`;
                          return (
                            <>
                              <i
                                id={importIconId}
                                className='fa fa-file-import text-ThemeTextLight'
                                style={{ fontSize: '0.85rem', cursor: 'help' }}
                              />
                              <UncontrolledTooltip placement='top' target={importIconId}>
                                Imported
                              </UncontrolledTooltip>
                            </>
                          );
                        })()}
                      </>
                    );
                  })()}
                  <StatusBadge
                    status={activity.calculationStatus}
                    notices={activity.calculationNotices}
                    activityId={activity._id}
                  />
                </div>
              </td>
              {showAddedCol && (
                <td className='tw:text-nowrap'>
                  {(() => {
                    const addedId = `added-date-${activity._id}`;
                    return (
                      <>
                        <small id={addedId} className='text-ThemeTextLight' style={{ cursor: 'default' }}>
                          {formatDateRelative(activity.created)}
                        </small>
                        <UncontrolledTooltip placement='top' target={addedId}>
                          {formatDate(activity.created)}
                        </UncontrolledTooltip>
                      </>
                    );
                  })()}
                </td>
              )}
              <td onClick={(e) => e.stopPropagation()}>
                {(onEdit || onDelete || onRecalculate || onViewCalculation || onExport) && (
                <UncontrolledDropdown>
                  <DropdownToggle color='link' className='p-1 text-ThemeLight tw:hover:!bg-primary tw:hover:!text-white'>
                    <i className='fa fa-ellipsis-v' />
                  </DropdownToggle>
                  <DropdownMenu end container='body'>
                    {onViewCalculation && activity.calculatedCO2e !== undefined && (
                      <>
                        <DropdownItem onClick={() => onViewCalculation(activity)}>
                          <i className='fa fa-calculator me-2' />
                          Calculation Details
                        </DropdownItem>
                        <DropdownItem divider />
                      </>
                    )}
                    {onEdit && (
                      <DropdownItem onClick={() => onEdit(activity._id)}>
                        <i className='fa fa-edit me-2' />
                        Edit
                      </DropdownItem>
                    )}
                    {onRecalculate && (
                      <DropdownItem
                        onClick={() => onRecalculate(activity._id)}
                        disabled={recalculatingActivityId === activity._id}
                      >
                        {recalculatingActivityId === activity._id ? (
                          <>
                            <i className='fa fa-spinner fa-spin me-2' />
                            Recalculating...
                          </>
                        ) : (
                          <>
                            <i className='fa fa-rotate me-2' />
                            Recalculate
                          </>
                        )}
                      </DropdownItem>
                    )}
                    {onExport && (
                      <DropdownItem onClick={() => onExport(activity._id)}>
                        <i className='fa fa-download me-2' />
                        Export to Excel
                      </DropdownItem>
                    )}
                    {onDelete && (
                      <>
                        {(onEdit || onRecalculate || onExport) && <DropdownItem divider />}
                        <DropdownItem className='text-danger' onClick={() => onDelete(activity._id)}>
                          <i className='fa fa-trash me-2 text-danger' />
                          Delete
                        </DropdownItem>
                      </>
                    )}
                  </DropdownMenu>
                </UncontrolledDropdown>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
